import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { getClientesConAreas } from '../../services/comercialClienteService';
import {
  getConversaciones,
  getMensajesByConversacion,
  tomarConversacion,
  devolverAIA,
  cerrarConversacion,
  reabrirConversacion,
  vincularConversacionCliente,
  markMensajesLeidos,
  enviarWhatsAppManual,
  enviarInstagramManual,
  enviarEmailManual,
} from '../../services/comercialService';

const ACCENT = '#F97316';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatRelTime(ts) {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

function formatHora(ts) {
  if (!ts) return '';
  return new Date(ts)?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function formatFechaCompleta(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const hoy = new Date();
  const ayer = new Date(hoy);
  ayer?.setDate(hoy?.getDate() - 1);
  if (d?.toDateString() === hoy?.toDateString()) return 'Hoy';
  if (d?.toDateString() === ayer?.toDateString()) return 'Ayer';
  return d?.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
}

function isSameDay(ts1, ts2) {
  if (!ts1 || !ts2) return false;
  const a = new Date(ts1);
  const b = new Date(ts2);
  return a?.getFullYear() === b?.getFullYear() &&
    a?.getMonth() === b?.getMonth() &&
    a?.getDate() === b?.getDate();
}

function formatTelefono(tel) {
  if (!tel) return '';
  const clean = tel?.replace(/\D/g, '');
  if (clean?.length === 9) return `+34 ${clean?.slice(0, 3)} ${clean?.slice(3, 6)} ${clean?.slice(6)}`;
  if (clean?.length === 11 && clean?.startsWith('34')) return `+${clean?.slice(0, 2)} ${clean?.slice(2, 5)} ${clean?.slice(5, 8)} ${clean?.slice(8)}`;
  return tel;
}

function getNombreConv(conv) {
  if (conv?.clientes?.nombre_comercial) return conv?.clientes?.nombre_comercial;
  if (conv?.clientes?.nombre) return conv?.clientes?.nombre;
  if (conv?.metadata?.instagram_username) return `@${conv.metadata.instagram_username}`;
  if (conv?.metadata?.instagram_name) return conv.metadata.instagram_name;
  if (conv?.metadata?.nombre_email) return conv.metadata.nombre_email;
  if (conv?.metadata?.email_remitente) return conv.metadata.email_remitente;
  if (conv?.metadata?.nombre_whatsapp) return conv?.metadata?.nombre_whatsapp;
  if (conv?.canal === 'instagram') return 'Contacto Instagram';
  if (conv?.canal === 'facebook') return 'Contacto Facebook';
  if (conv?.canal === 'email') return 'Contacto Email';
  return 'Contacto WhatsApp';
}

function getIniciales(nombre) {
  if (!nombre) return '?';
  const parts = nombre?.trim()?.split(/\s+/);
  if (parts?.length >= 2) return (parts?.[0]?.[0] + parts?.[1]?.[0])?.toUpperCase();
  return nombre?.slice(0, 2)?.toUpperCase();
}

function getAvatarColor(nombre) {
  const colors = ['#F97316', '#8B5CF6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#3B82F6'];
  let hash = 0;
  for (let i = 0; i < (nombre || '')?.length; i++) hash = nombre?.charCodeAt(i) + ((hash << 5) - hash);
  return colors?.[Math.abs(hash) % colors?.length];
}

// ── Config ────────────────────────────────────────────────────────────────────

const canalesFiltro = [
  { key: 'todos', label: 'Todos', icon: 'Inbox', color: ACCENT },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'MessageCircle', color: '#25D366' },
  { key: 'instagram', label: 'Instagram', icon: 'Instagram', color: '#E1306C' },
  { key: 'facebook', label: 'Facebook', icon: 'Facebook', color: '#1877F2' },
  { key: 'email', label: 'Email', icon: 'Mail', color: '#60A5FA' },
  { key: 'no_leidos', label: 'No leídos', icon: 'BellDot', color: '#FBBF24' },
  { key: 'atencion', label: 'Requiere atención', icon: 'AlertCircle', color: '#F87171' },
];

const canalIconos = {
  whatsapp: { icon: 'MessageCircle', color: '#25D366' },
  instagram: { icon: 'Instagram', color: '#E1306C' },
  facebook: { icon: 'Facebook', color: '#1877F2' },
  email: { icon: 'Mail', color: '#60A5FA' },
  web: { icon: 'Globe', color: '#A78BFA' },
  manual: { icon: 'PenLine', color: '#94A3B8' },
};

const estadoConfig = {
  automatico: { color: '#34D399', label: 'Automático' },
  requiere_atencion: { color: '#FBBF24', label: 'Requiere atención' },
  urgente: { color: '#F87171', label: 'Urgente' },
  modo_humano: { color: '#60A5FA', label: 'Modo humano' },
  cerrado: { color: '#6B7280', label: 'Cerrado' },
};

const estadoMsgIcono = {
  enviando: { icon: 'Clock', color: '#6B7280' },
  enviado: { icon: 'Check', color: '#9CA3AF' },
  entregado: { icon: 'CheckCheck', color: '#9CA3AF' },
  leido: { icon: 'CheckCheck', color: '#60A5FA' },
  error: { icon: 'AlertCircle', color: '#F87171' },
};

// ── Sub-components ────────────────────────────────────────────────────────────

function Avatar({ nombre, size = 36 }) {
  const iniciales = getIniciales(nombre);
  const color = getAvatarColor(nombre);
  return (
    <div className="rounded-full flex items-center justify-center flex-shrink-0 font-bold"
      style={{ width: size, height: size, background: `${color}22`, border: `1.5px solid ${color}44`, color, fontSize: size * 0.38 }}>
      {iniciales}
    </div>
  );
}

function CanalBadge({ canal, small }) {
  const cfg = canalIconos?.[canal] || { icon: 'MessageSquare', color: '#94A3B8' };
  return (
    <div className="flex items-center gap-1">
      <Icon name={cfg?.icon} size={small ? 11 : 13} style={{ color: cfg?.color }} />
      {!small && <span className="font-oranienbaum text-xs capitalize" style={{ color: cfg?.color }}>{canal}</span>}
    </div>
  );
}

function EstadoBadge({ estado }) {
  const cfg = estadoConfig?.[estado] || { color: '#6B7280', label: estado };
  return (
    <span className="font-oranienbaum text-xs px-2 py-0.5 rounded-full"
      style={{ background: `${cfg?.color}18`, color: cfg?.color, border: `1px solid ${cfg?.color}30` }}>
      {cfg?.label}
    </span>
  );
}

function ModoIndicador({ modo }) {
  if (modo === 'humano') {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
        style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.25)' }}>
        <span className="text-xs">👤</span>
        <span className="font-oranienbaum text-blue-400 text-xs font-semibold">Modo humano</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
      style={{ background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.20)' }}>
      <span className="text-xs">✨</span>
      <span className="font-oranienbaum text-purple-400 text-xs">IA activa</span>
    </div>
  );
}

function MsgEstadoIcon({ estado }) {
  if (!estado) return null;
  const cfg = estadoMsgIcono?.[estado];
  if (!cfg) return null;
  return <Icon name={cfg?.icon} size={11} style={{ color: cfg?.color }} />;
}

function HighlightText({ text, query }) {
  if (!query || !text) return <span>{text}</span>;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text?.split(regex);
  return (
    <span>
      {parts?.map((part, i) =>
        regex?.test(part)
          ? <mark key={i} style={{ background: 'rgba(249,115,22,0.35)', color: '#fff', borderRadius: 2 }}>{part}</mark>
          : part
      )}
    </span>
  );
}

// ── Burbuja de mensaje ────────────────────────────────────────────────────────

function BurbujaMensaje({ msg, searchQuery }) {
  const esSaliente = msg?.direccion === 'saliente';
  const esSistema = msg?.autor_tipo === 'sistema';
  const hora = formatHora(msg?.enviado_at || msg?.recibido_at || msg?.created_at);

  if (esSistema) {
    return (
      <div className="flex justify-center my-3">
        <span className="font-oranienbaum text-xs px-3 py-1 rounded-full"
          style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.30)', border: '1px solid rgba(255,255,255,0.07)' }}>
          {msg?.contenido || `[${msg?.tipo_contenido}]`}
        </span>
      </div>
    );
  }

  // Colores según autor
  let bubbleBg, bubbleBorder, textColor;
  if (esSaliente) {
    if (msg?.autor_tipo === 'ia') {
      bubbleBg = 'rgba(139,92,246,0.12)';
      bubbleBorder = 'rgba(139,92,246,0.25)';
    } else {
      bubbleBg = 'rgba(249,115,22,0.10)';
      bubbleBorder = 'rgba(249,115,22,0.22)';
    }
    textColor = 'rgba(255,255,255,0.88)';
  } else {
    bubbleBg = 'rgba(255,255,255,0.06)';
    bubbleBorder = 'rgba(255,255,255,0.10)';
    textColor = 'rgba(255,255,255,0.85)';
  }

  return (
    <div className={`flex ${esSaliente ? 'justify-end' : 'justify-start'} mb-1.5 px-4`}>
      <div style={{ maxWidth: '72%' }}>
        {/* Etiqueta autor (solo saliente IA/humano) */}
        {esSaliente && (
          <div className="flex justify-end mb-0.5">
            <span className="font-oranienbaum text-xs"
              style={{ color: msg?.autor_tipo === 'ia' ? 'rgba(167,139,250,0.7)' : 'rgba(249,115,22,0.7)' }}>
              {msg?.autor_tipo === 'ia' ? '✨ IA' : '👤 Humano'}
            </span>
          </div>
        )}

        {/* Burbuja */}
        <div className="rounded-2xl px-3.5 py-2.5"
          style={{ background: bubbleBg, border: `1px solid ${bubbleBorder}`, borderRadius: esSaliente ? '18px 4px 18px 18px' : '4px 18px 18px 18px' }}>

          {/* Contenido */}
          {(msg?.tipo_contenido === 'texto' || !msg?.tipo_contenido) ? (
            <p className="font-oranienbaum text-sm leading-relaxed whitespace-pre-wrap" style={{ color: textColor }}>
              <HighlightText text={msg?.contenido || '—'} query={searchQuery} />
            </p>
          ) : (
            <div className="flex items-center gap-2">
              <Icon name={
                msg?.tipo_contenido === 'audio' ? 'Mic' :
                msg?.tipo_contenido === 'imagen' ? 'Image' :
                msg?.tipo_contenido === 'video' ? 'Video' :
                msg?.tipo_contenido === 'documento' ? 'FileText' : 'File'
              } size={15} style={{ color: 'rgba(255,255,255,0.45)' }} />
              <span className="font-oranienbaum text-xs italic" style={{ color: 'rgba(255,255,255,0.45)' }}>
                [{msg?.tipo_contenido}]
              </span>
            </div>
          )}

          {/* Transcripción audio */}
          {msg?.transcripcion && (
            <p className="font-oranienbaum text-xs mt-1.5 pt-1.5 italic leading-relaxed"
              style={{ color: 'rgba(255,255,255,0.40)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              📝 {msg?.transcripcion}
            </p>
          )}

          {/* Hora + estado */}
          <div className={`flex items-center gap-1 mt-1 ${esSaliente ? 'justify-end' : 'justify-start'}`}>
            <span className="font-oranienbaum text-xs" style={{ color: 'rgba(255,255,255,0.28)' }}>{hora}</span>
            {esSaliente && <MsgEstadoIcon estado={msg?.estado} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Separador de fecha ────────────────────────────────────────────────────────

function FechaSeparador({ fecha }) {
  return (
    <div className="flex items-center gap-3 px-4 my-4">
      <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
      <span className="font-oranienbaum text-xs px-3 py-1 rounded-full flex-shrink-0"
        style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.08)' }}>
        {fecha}
      </span>
      <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
    </div>
  );
}

// ── Panel de detalles (desplegable) ───────────────────────────────────────────

function DetallesPanel({ conv, onClose }) {
  return (
    <div className="absolute top-0 right-0 h-full w-72 z-20 flex flex-col"
      style={{ background: '#111', border: '1px solid rgba(255,255,255,0.10)', borderRadius: '0 12px 12px 0' }}>
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <span className="font-oranienbaum text-white/70 text-sm font-semibold">Detalles</span>
        <button onClick={onClose} className="text-white/30 hover:text-white/70 transition-colors">
          <Icon name="X" size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {[
          { label: 'Estado', value: <EstadoBadge estado={conv?.estado} /> },
          { label: 'Modo', value: <ModoIndicador modo={conv?.modo} /> },
          { label: 'Intención', value: conv?.intencion_actual || '—' },
          { label: 'Departamento', value: conv?.departamento ? <span className="capitalize">{conv?.departamento}</span> : '—' },
          { label: 'Prioridad', value: conv?.prioridad ?? '—' },
          { label: 'Canal', value: <div className="flex items-center gap-1.5"><CanalBadge canal={conv?.canal} /></div> },
          { label: 'Pedido', value: <span className="italic text-white/30">Sin pedido vinculado</span> },
          { label: 'Creada', value: conv?.created_at ? new Date(conv.created_at)?.toLocaleDateString('es-ES') : '—' },
        ]?.map(({ label, value }) => (
          <div key={label} className="flex items-start justify-between gap-2">
            <span className="font-oranienbaum text-white/30 text-xs flex-shrink-0">{label}</span>
            <span className="font-oranienbaum text-white/70 text-xs text-right">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

const ComercialComunicaciones = () => {
  const navigate = useNavigate();

  // Estado bandeja
  const [canalActivo, setCanalActivo] = useState('todos');
  const [showVincularCliente, setShowVincularCliente] = useState(false);
  const [clientesVincular, setClientesVincular] = useState([]);
  const [busquedaClienteVincular, setBusquedaClienteVincular] = useState('');
  const [vinculandoCliente, setVinculandoCliente] = useState(false);
  const [busquedaBandeja, setBusquedaBandeja] = useState('');
  const [conversaciones, setConversaciones] = useState([]);
  const [unreadMap, setUnreadMap] = useState({});
  const [ultimoMsgMap, setUltimoMsgMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estado chat
  const [convSeleccionada, setConvSeleccionada] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [accionLoading, setAccionLoading] = useState(false);
  const [accionError, setAccionError] = useState(null);
  const [mensajeRespuesta, setMensajeRespuesta] = useState('');
  const [enviandoMensaje, setEnviandoMensaje] = useState(false);

  // UI chat
  const [showDetalles, setShowDetalles] = useState(false);
  const [searchChat, setSearchChat] = useState('');
  const [showSearchChat, setShowSearchChat] = useState(false);
  const [showNuevosMensajes, setShowNuevosMensajes] = useState(false);
  const [mobileView, setMobileView] = useState('bandeja'); // 'bandeja' | 'chat'

  // Refs
  const mensajesEndRef = useRef(null);
  const mensajesContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);

  // ── Carga conversaciones ──────────────────────────────────────────────────

  const cargarConversaciones = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getConversaciones({ canal: canalActivo });
      setConversaciones(data);
    } catch (err) {
      console.error('[Comunicaciones] cargarConversaciones error:', err);
      setError(err?.message || 'Error al cargar conversaciones');
    } finally {
      setLoading(false);
    }
  }, [canalActivo]);

  useEffect(() => {
    cargarConversaciones();
  }, [cargarConversaciones]);

  // ── Supabase Realtime: conversaciones ─────────────────────────────────────

  useEffect(() => {
    const channel = supabase?.channel('comercial-conversaciones')?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'conversaciones',
      }, () => {
        cargarConversaciones();
      })?.subscribe();

    return () => { supabase?.removeChannel(channel); };
  }, [cargarConversaciones]);

  // ── Supabase Realtime: mensajes de conversación activa ────────────────────

  useEffect(() => {
    if (!convSeleccionada?.id) return;

    const channel = supabase?.channel(`mensajes-${convSeleccionada?.id}`)?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'mensajes',
        filter: `conversacion_id=eq.${convSeleccionada?.id}`,
      }, (payload) => {
        const nuevoMsg = payload?.new;
        if (!nuevoMsg) return;
        setMensajes(prev => {
          const existe = prev?.some(m => m?.id === nuevoMsg?.id);
          if (existe) return prev;
          const updated = [...prev, nuevoMsg];
          // Mantener siempre la conversación visible en el mensaje más reciente.
          // Esta bandeja es operativa: cuando entra o sale un mensaje nuevo,
          // debe quedar a la vista sin que el usuario tenga que hacer scroll.
          isAtBottomRef.current = true;
          setShowNuevosMensajes(false);
          setTimeout(() => mensajesEndRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 50);
          return updated;
        });
        // Actualizar último mensaje en bandeja
        setUltimoMsgMap(prev => ({ ...prev, [convSeleccionada?.id]: nuevoMsg }));
        // Marcar como leído si es entrante
        if (nuevoMsg?.direccion === 'entrante' && isAtBottomRef?.current) {
          markMensajesLeidos(convSeleccionada?.id);
        }
      })?.subscribe();

    return () => { supabase?.removeChannel(channel); };
  }, [convSeleccionada?.id]);

  // ── Respaldo de sincronización automática ─────────────────────────────────
  // Realtime sigue siendo la vía principal. Este refresco ligero evita que la
  // bandeja o el chat se queden desactualizados si se pierde un evento Realtime.
  useEffect(() => {
    const sync = async () => {
      if (document?.visibilityState !== 'visible') return;
      try {
        await cargarConversaciones();
        if (convSeleccionada?.id) {
          const msgs = await getMensajesByConversacion(convSeleccionada.id);
          setMensajes(prev => {
            const prevLast = prev?.[prev.length - 1]?.id;
            const nextLast = msgs?.[msgs.length - 1]?.id;
            const sinCambios = prev?.length === msgs?.length && prevLast === nextLast;
            if (!sinCambios) {
              isAtBottomRef.current = true;
              setShowNuevosMensajes(false);
              setTimeout(() => mensajesEndRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 50);
            }
            return sinCambios ? prev : msgs;
          });
        }
      } catch (err) {
        console.warn('[Comunicaciones] sincronización automática:', err);
      }
    };

    const timer = window.setInterval(sync, 5000);
    const onVisible = () => {
      if (document?.visibilityState === 'visible') sync();
    };
    document?.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(timer);
      document?.removeEventListener('visibilitychange', onVisible);
    };
  }, [cargarConversaciones, convSeleccionada?.id]);

  // ── Scroll tracking ───────────────────────────────────────────────────────

  const handleScroll = useCallback(() => {
    const el = mensajesContainerRef?.current;
    if (!el) return;
    const distFromBottom = el?.scrollHeight - el?.scrollTop - el?.clientHeight;
    isAtBottomRef.current = distFromBottom < 80;
    if (isAtBottomRef?.current) setShowNuevosMensajes(false);
  }, []);

  const scrollToBottom = useCallback((smooth = true) => {
    mensajesEndRef?.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    setShowNuevosMensajes(false);
  }, []);

  // ── Seleccionar conversación ──────────────────────────────────────────────

  const seleccionarConversacion = async (conv) => {
    setConvSeleccionada(conv);
    setLoadingDetalle(true);
    setMensajes([]);
    
    setShowDetalles(false);
    setShowSearchChat(false);
    setSearchChat('');
    setShowNuevosMensajes(false);
    setMensajeRespuesta('');
    
    setMobileView('chat');
    try {
      const msgs = await getMensajesByConversacion(conv?.id);
      setMensajes(msgs);
      
      // Marcar como leídos
      await markMensajesLeidos(conv?.id);
      setUnreadMap(prev => ({ ...prev, [conv?.id]: 0 }));
      // Al abrir una conversación, mostrar siempre el último mensaje.
      isAtBottomRef.current = true;
      setTimeout(() => scrollToBottom(false), 120);
    } catch (err) {
      console.error('[Comunicaciones] seleccionarConversacion error:', err);
    } finally {
      setLoadingDetalle(false);
    }
  };

  // ── Acciones ──────────────────────────────────────────────────────────────

  const handleTomarConversacion = async () => {
    if (!convSeleccionada) return;
    setAccionLoading(true);
    setAccionError(null);
    try {
      await tomarConversacion(convSeleccionada?.id);
      await cargarConversaciones();
      setConvSeleccionada(prev => ({ ...prev, modo: 'humano', estado: 'modo_humano' }));
    } catch (err) {
      console.error('[Comunicaciones] tomarConversacion error:', err);
      setAccionError(err?.message || 'Error al tomar conversación');
    } finally {
      setAccionLoading(false);
    }
  };

  const handleEnviarMensaje = async () => {
    const texto = mensajeRespuesta?.trim();
    if (!convSeleccionada?.id || !texto || enviandoMensaje) return;

    if (!['whatsapp', 'instagram', 'email'].includes(convSeleccionada?.canal)) {
      setAccionError('El envío manual todavía no está conectado para este canal.');
      return;
    }
    if (convSeleccionada?.estado === 'cerrado') {
      setAccionError('La conversación está cerrada.');
      return;
    }
    if (convSeleccionada?.modo !== 'humano') {
      setAccionError('Toma la conversación antes de responder manualmente.');
      return;
    }

    setEnviandoMensaje(true);
    setAccionError(null);

    try {
      const resultado = convSeleccionada?.canal === 'instagram'
        ? await enviarInstagramManual(convSeleccionada?.id, texto)
        : convSeleccionada?.canal === 'email'
          ? await enviarEmailManual(convSeleccionada?.id, texto)
          : await enviarWhatsAppManual(convSeleccionada?.id, texto);
      const mensajeGuardado = resultado?.mensaje;

      if (mensajeGuardado?.id) {
        setMensajes(prev => {
          if (prev?.some(m => m?.id === mensajeGuardado?.id)) return prev;
          return [...prev, mensajeGuardado];
        });
        setUltimoMsgMap(prev => ({ ...prev, [convSeleccionada?.id]: mensajeGuardado }));
      }

      setMensajeRespuesta('');
      setTimeout(() => scrollToBottom(true), 50);
      cargarConversaciones();
    } catch (err) {
      console.error('[Comunicaciones] enviar mensaje error:', err);
      setAccionError(err?.message || 'No se pudo enviar el mensaje');
    } finally {
      setEnviandoMensaje(false);
    }
  };

  const handleKeyDownRespuesta = (event) => {
    if (event?.key === 'Enter' && !event?.shiftKey) {
      event?.preventDefault();
      handleEnviarMensaje();
    }
  };

  const handleDevolverAIA = async () => {
    if (!convSeleccionada) return;
    setAccionLoading(true);
    setAccionError(null);
    try {
      await devolverAIA(convSeleccionada?.id);
      await cargarConversaciones();
      setConvSeleccionada(prev => ({ ...prev, modo: 'ia', estado: 'automatico' }));
    } catch (err) {
      console.error('[Comunicaciones] devolverAIA error:', err);
      setAccionError(err?.message || 'Error al devolver a IA');
    } finally {
      setAccionLoading(false);
    }
  };

  const handleCerrarConversacion = async () => {
    if (!convSeleccionada) return;
    setAccionLoading(true);
    setAccionError(null);
    try {
      await cerrarConversacion(convSeleccionada?.id);
      await cargarConversaciones();
      setConvSeleccionada(prev => ({ ...prev, estado: 'cerrado', cerrada_at: new Date()?.toISOString() }));
    } catch (err) {
      console.error('[Comunicaciones] cerrarConversacion error:', err);
      setAccionError(err?.message || 'Error al cerrar conversación');
    } finally {
      setAccionLoading(false);
    }
  };

  const handleReabrirConversacion = async () => {
    if (!convSeleccionada) return;
    setAccionLoading(true);
    setAccionError(null);
    try {
      await reabrirConversacion(convSeleccionada.id);
      await cargarConversaciones();
      setConvSeleccionada(prev => ({ ...prev, modo: 'humano', estado: 'modo_humano', cerrada_at: null }));
    } catch (err) {
      setAccionError(err?.message || 'No se pudo reabrir la conversación');
    } finally {
      setAccionLoading(false);
    }
  };

  const handleAbrirVincularCliente = async () => {
    setAccionError(null);
    try {
      const clientes = await getClientesConAreas();
      setClientesVincular((clientes || []).filter(c => c?.activo !== false));
      setBusquedaClienteVincular('');
      setShowVincularCliente(true);
    } catch (err) {
      setAccionError(err?.message || 'No se pudieron cargar los clientes');
    }
  };

  const handleVincularCliente = async (cliente) => {
    if (!convSeleccionada || !cliente?.id || vinculandoCliente) return;
    setVinculandoCliente(true);
    setAccionError(null);
    try {
      await vincularConversacionCliente(convSeleccionada, cliente.id);
      const clienteLigero = { id: cliente.id, nombre: cliente.nombre, nombre_comercial: cliente.nombre_comercial, telefono: cliente.telefono, email: cliente.email };
      setConvSeleccionada(prev => ({ ...prev, cliente_id: cliente.id, clientes: clienteLigero }));
      setShowVincularCliente(false);
      await cargarConversaciones();
    } catch (err) {
      setAccionError(err?.message || 'No se pudo vincular el cliente');
    } finally {
      setVinculandoCliente(false);
    }
  };


  const convsFiltradas = conversaciones?.filter(c => {
    if (!busquedaBandeja) return true;
    const q = busquedaBandeja?.toLowerCase();
    const nombre = getNombreConv(c)?.toLowerCase();
    return nombre?.includes(q) || c?.canal?.includes(q) || c?.intencion_actual?.toLowerCase()?.includes(q);
  });

  // ── Mensajes filtrados por búsqueda ───────────────────────────────────────

  const mensajesFiltrados = searchChat
    ? mensajes?.filter(m => m?.contenido?.toLowerCase()?.includes(searchChat?.toLowerCase()))
    : mensajes;

  // ── Renderizado mensajes con separadores de fecha ─────────────────────────

  const renderMensajesConFechas = () => {
    const items = [];
    let lastDate = null;
    mensajesFiltrados?.forEach((msg, idx) => {
      const ts = msg?.enviado_at || msg?.recibido_at || msg?.created_at;
      if (!isSameDay(ts, lastDate)) {
        items?.push(<FechaSeparador key={`fecha-${idx}`} fecha={formatFechaCompleta(ts)} />);
        lastDate = ts;
      }
      items?.push(<BurbujaMensaje key={msg?.id} msg={msg} searchQuery={searchChat} />);
    });
    return items;
  };

  // ── Nombre y datos del contacto ───────────────────────────────────────────

  const nombreConv = convSeleccionada ? getNombreConv(convSeleccionada) : '';
  const telefonoConv = convSeleccionada?.clientes?.telefono || convSeleccionada?.metadata?.telefono || '';
  const esContactoNoVinculado = convSeleccionada && !convSeleccionada?.cliente_id;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col"
      style={{ height: '100vh', background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>

      {/* Header superior */}
      <div className="flex-shrink-0 px-4 pt-4 pb-3 flex items-center justify-between gap-4"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div>
          <nav className="flex items-center gap-2 text-xs font-oranienbaum text-white/30 mb-1">
            <span>🤝 Comercial</span>
            <Icon name="ChevronRight" size={12} />
            <span className="text-white/60">Comunicaciones</span>
          </nav>
          <div className="flex items-center gap-2">
            <span className="text-xl">💬</span>
            <h1 className="font-oranienbaum font-bold text-white text-xl tracking-wide">Comunicaciones Omnicanal</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
          style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.20)' }}>
          <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
          <span className="font-oranienbaum text-orange-400 text-xs tracking-widest uppercase hidden sm:block">WhatsApp · Instagram · Email conectados</span>
        </div>
      </div>

      {/* Layout principal 3 columnas */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Columna 1: Filtros de canal ── */}
        <div className={`flex-shrink-0 flex flex-col ${mobileView !== 'bandeja' ? 'hidden md:flex' : 'flex'}`}
          style={{ width: 200, borderRight: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.20)' }}>
          <div className="p-3 flex-1 overflow-y-auto">
            <p className="font-oranienbaum text-white/25 text-xs uppercase tracking-widest px-2 py-1 mb-1">Canales</p>
            {canalesFiltro?.map(c => (
              <button key={c?.key}
                onClick={() => { setCanalActivo(c?.key); setConvSeleccionada(null); setMobileView('bandeja'); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg font-oranienbaum text-sm transition-all text-left mb-0.5 ${canalActivo === c?.key ? 'font-semibold' : 'text-white/50 hover:text-white/80 hover:bg-white/[0.04]'}`}
                style={canalActivo === c?.key ? { background: `${c?.color}14`, color: c?.color, border: `1px solid ${c?.color}22` } : { border: '1px solid transparent' }}>
                <Icon name={c?.icon} size={14} style={{ color: canalActivo === c?.key ? c?.color : undefined }} />
                {c?.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Columna 2: Bandeja de conversaciones ── */}
        <div className={`flex flex-col ${mobileView === 'chat' ? 'hidden md:flex' : 'flex'}`}
          style={{ width: 300, minWidth: 260, borderRight: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.10)' }}>

          {/* Búsqueda bandeja */}
          <div className="p-3 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="relative">
              <Icon name="Search" size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
              <input type="text" value={busquedaBandeja} onChange={e => setBusquedaBandeja(e?.target?.value)}
                placeholder="Buscar conversación…"
                className="w-full pl-8 pr-3 py-2 rounded-lg font-oranienbaum text-sm text-white/70 placeholder-white/20 outline-none"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }} />
            </div>
          </div>

          {/* Lista conversaciones */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-5 h-5 border-2 border-orange-400/30 border-t-orange-400 rounded-full animate-spin" />
              </div>
            ) : error ? (
              <div className="p-3">
                <div className="rounded-lg p-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.18)' }}>
                  <p className="font-oranienbaum text-red-400 text-xs">{error}</p>
                </div>
              </div>
            ) : convsFiltradas?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 p-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                  style={{ background: 'rgba(249,115,22,0.07)', border: '1px solid rgba(249,115,22,0.13)' }}>
                  <Icon name="MessageSquare" size={22} className="text-orange-400/50" />
                </div>
                <p className="font-oranienbaum text-white/40 text-sm font-semibold mb-1">Bandeja vacía</p>
                <p className="font-oranienbaum text-white/20 text-xs text-center leading-relaxed">
                  Las conversaciones aparecerán aquí cuando se conecten los canales.
                </p>
              </div>
            ) : (
              <div>
                {convsFiltradas?.map(conv => {
                  const nombre = getNombreConv(conv);
                  const unread = unreadMap?.[conv?.id] || 0;
                  const esDestacada = conv?.estado === 'requiere_atencion' || conv?.estado === 'urgente';
                  const isSelected = convSeleccionada?.id === conv?.id;
                  return (
                    <button key={conv?.id} onClick={() => seleccionarConversacion(conv)}
                      className="w-full text-left px-3 py-3 transition-all hover:bg-white/[0.025]"
                      style={{
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        background: isSelected ? 'rgba(249,115,22,0.06)' : undefined,
                        borderLeft: isSelected ? `2px solid ${ACCENT}` : '2px solid transparent',
                      }}>
                      <div className="flex items-start gap-2.5">
                        <Avatar nombre={nombre} size={38} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1 mb-0.5">
                            <span className="font-oranienbaum text-sm font-semibold truncate"
                              style={{ color: esDestacada ? '#FBBF24' : 'rgba(255,255,255,0.88)' }}>
                              {nombre}
                            </span>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span className="font-oranienbaum text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
                                {formatRelTime(conv?.ultimo_mensaje_at)}
                              </span>
                              {unread > 0 && (
                                <span className="font-oranienbaum text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center"
                                  style={{ background: ACCENT, color: '#fff', fontSize: 10 }}>
                                  {unread}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Último mensaje preview */}
                          <p className="font-oranienbaum text-xs truncate mb-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
                            {ultimoMsgMap?.[conv?.id]?.contenido || conv?.intencion_actual || '—'}
                          </p>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <CanalBadge canal={conv?.canal} small />
                            <span className="font-oranienbaum text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>·</span>
                            <span className="font-oranienbaum text-xs"
                              style={{ color: estadoConfig?.[conv?.estado]?.color || '#6B7280' }}>
                              {estadoConfig?.[conv?.estado]?.label || conv?.estado}
                            </span>
                            {esDestacada && <Icon name="AlertCircle" size={11} className="text-amber-400" />}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Columna 3: Chat ── */}
        <div className={`flex-1 flex flex-col overflow-hidden relative ${mobileView === 'bandeja' && !convSeleccionada ? 'hidden md:flex' : 'flex'}`}>

          {!convSeleccionada ? (
            /* Estado vacío */
            <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center"
                style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.12)' }}>
                <Icon name="MessageSquareDashed" size={36} className="text-orange-400/40" />
              </div>
              <div className="text-center">
                <p className="font-oranienbaum text-white/40 text-base font-semibold mb-1">Selecciona una conversación</p>
                <p className="font-oranienbaum text-white/20 text-sm">Elige una conversación de la bandeja para ver el chat</p>
              </div>
            </div>
          ) : (
            <>
              {/* ── CABECERA FIJA ── */}
              <div className="flex-shrink-0 px-4 py-3 flex items-center gap-3"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.30)' }}>

                {/* Botón volver (mobile) */}
                <button onClick={() => setMobileView('bandeja')}
                  className="md:hidden text-white/40 hover:text-white/70 transition-colors mr-1">
                  <Icon name="ArrowLeft" size={18} />
                </button>

                <Avatar nombre={nombreConv} size={40} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-oranienbaum text-white font-semibold text-base truncate">{nombreConv}</span>
                    {esContactoNoVinculado && (
                      <span className="font-oranienbaum text-xs px-1.5 py-0.5 rounded"
                        style={{ background: 'rgba(251,191,36,0.10)', color: '#FBBF24', border: '1px solid rgba(251,191,36,0.20)' }}>
                        No vinculado
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {telefonoConv && (
                      <span className="font-oranienbaum text-xs" style={{ color: 'rgba(255,255,255,0.40)' }}>
                        {formatTelefono(telefonoConv)}
                      </span>
                    )}
                    {telefonoConv && <span className="text-white/20 text-xs">·</span>}
                    <CanalBadge canal={convSeleccionada?.canal} />
                    <span className="text-white/20 text-xs">·</span>
                    <span className="font-oranienbaum text-xs" style={{ color: estadoConfig?.[convSeleccionada?.estado]?.color || '#6B7280' }}>
                      {estadoConfig?.[convSeleccionada?.estado]?.label}
                    </span>
                  </div>
                </div>

                {/* Indicadores derecha */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <ModoIndicador modo={convSeleccionada?.modo} />

                  {/* Búsqueda en conversación */}
                  <button onClick={() => { setShowSearchChat(s => !s); setSearchChat(''); }}
                    className="p-2 rounded-lg transition-all"
                    style={{ background: showSearchChat ? 'rgba(249,115,22,0.12)' : 'rgba(255,255,255,0.04)', color: showSearchChat ? ACCENT : 'rgba(255,255,255,0.40)' }}>
                    <Icon name="Search" size={15} />
                  </button>

                  {/* Detalles */}
                  <button onClick={() => setShowDetalles(s => !s)}
                    className="px-3 py-1.5 rounded-lg font-oranienbaum text-xs transition-all"
                    style={{ background: showDetalles ? 'rgba(249,115,22,0.12)' : 'rgba(255,255,255,0.05)', color: showDetalles ? ACCENT : 'rgba(255,255,255,0.50)', border: `1px solid ${showDetalles ? 'rgba(249,115,22,0.25)' : 'rgba(255,255,255,0.08)'}` }}>
                    Detalles
                  </button>

                  {/* Vincular cliente (si no vinculado) */}
                  {esContactoNoVinculado && (
                    <button onClick={handleAbrirVincularCliente} className="px-3 py-1.5 rounded-lg font-oranienbaum text-xs transition-all"
                      style={{ background: 'rgba(251,191,36,0.08)', color: '#FBBF24', border: '1px solid rgba(251,191,36,0.18)' }}>
                      Vincular cliente
                    </button>
                  )}

                  {/* Ver ficha */}
                  {convSeleccionada?.clientes?.id && (
                    <button onClick={() => navigate(`/comercial-clientes/${convSeleccionada?.clientes?.id}`)}
                      className="p-2 rounded-lg transition-all"
                      style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.40)' }}>
                      <Icon name="User" size={15} />
                    </button>
                  )}
                </div>
              </div>

              {/* Barra búsqueda en chat */}
              {showSearchChat && (
                <div className="flex-shrink-0 px-4 py-2 flex items-center gap-2"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.20)' }}>
                  <Icon name="Search" size={13} style={{ color: ACCENT }} />
                  <input type="text" value={searchChat} onChange={e => setSearchChat(e?.target?.value)}
                    placeholder="Buscar en esta conversación…"
                    autoFocus
                    className="flex-1 font-oranienbaum text-sm text-white/70 placeholder-white/25 outline-none bg-transparent" />
                  {searchChat && (
                    <span className="font-oranienbaum text-xs" style={{ color: 'rgba(255,255,255,0.30)' }}>
                      {mensajesFiltrados?.length} resultado{mensajesFiltrados?.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  <button onClick={() => { setSearchChat(''); setShowSearchChat(false); }}
                    className="text-white/30 hover:text-white/60 transition-colors">
                    <Icon name="X" size={14} />
                  </button>
                </div>
              )}

              {/* ── ÁREA DE MENSAJES ── */}
              <div ref={mensajesContainerRef} onScroll={handleScroll}
                className="flex-1 overflow-y-auto py-3"
                style={{ background: 'rgba(0,0,0,0.15)' }}>
                {loadingDetalle ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="w-5 h-5 border-2 border-orange-400/30 border-t-orange-400 rounded-full animate-spin" />
                  </div>
                ) : mensajes?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full py-12 gap-2">
                    <Icon name="MessageSquare" size={24} className="text-white/10" />
                    <p className="font-oranienbaum text-white/20 text-sm">Sin mensajes todavía</p>
                  </div>
                ) : (
                  <>
                    {renderMensajesConFechas()}

                  </>
                )}
                <div ref={mensajesEndRef} />
              </div>

              {/* Indicador nuevos mensajes */}
              {showNuevosMensajes && (
                <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-10">
                  <button onClick={() => scrollToBottom(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-full font-oranienbaum text-xs font-semibold shadow-lg transition-all"
                    style={{ background: ACCENT, color: '#fff' }}>
                    <Icon name="ArrowDown" size={13} />
                    Nuevos mensajes
                  </button>
                </div>
              )}

              {/* ── CAJA DE RESPUESTA FIJA ── */}
              <div className="flex-shrink-0 px-4 py-3"
                style={{ borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.30)' }}>

                {/* Error acciones */}
                {accionError && (
                  <div className="mb-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.18)' }}>
                    <p className="font-oranienbaum text-red-400 text-xs">{accionError}</p>
                  </div>
                )}

                {/* Botones modo IA/Humano */}
                {convSeleccionada?.estado !== 'cerrado' && (
                  <div className="flex items-center gap-2 mb-3">
                    {convSeleccionada?.modo !== 'humano' ? (
                      <button onClick={handleTomarConversacion} disabled={accionLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-oranienbaum text-xs font-semibold transition-all disabled:opacity-50"
                        style={{ background: 'rgba(96,165,250,0.10)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.22)' }}>
                        <Icon name="UserCheck" size={13} />
                        {accionLoading ? '…' : 'Tomar conversación'}
                      </button>
                    ) : (
                      <button onClick={handleDevolverAIA} disabled={accionLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-oranienbaum text-xs font-semibold transition-all disabled:opacity-50"
                        style={{ background: 'rgba(167,139,250,0.10)', color: '#A78BFA', border: '1px solid rgba(167,139,250,0.22)' }}>
                        <span className="text-xs">🤖</span>
                        {accionLoading ? '…' : 'Devolver a IA'}
                      </button>
                    )}
                    <button onClick={handleCerrarConversacion} disabled={accionLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-oranienbaum text-xs transition-all disabled:opacity-50"
                      style={{ background: 'rgba(107,114,128,0.08)', color: '#9CA3AF', border: '1px solid rgba(107,114,128,0.15)' }}>
                      <Icon name="CheckCircle" size={13} />
                      Cerrar
                    </button>
                  </div>
                )}

                {convSeleccionada?.estado === 'cerrado' && (
                  <div className="mb-3 flex items-center justify-center gap-3">
                    <span className="font-oranienbaum text-white/25 text-xs">Conversación cerrada</span>
                    <button onClick={handleReabrirConversacion} disabled={accionLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-oranienbaum text-xs font-semibold transition-all disabled:opacity-50"
                      style={{ background: 'rgba(96,165,250,0.10)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.22)' }}>
                      <Icon name="RotateCcw" size={13} />
                      {accionLoading ? '…' : 'Reabrir conversación'}
                    </button>
                  </div>
                )}

                {/* Input de respuesta */}
                <div className="flex items-end gap-2">
                  <div className="flex-1 rounded-xl px-3 py-2.5"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)' }}>
                    <textarea
                      value={mensajeRespuesta}
                      onChange={e => setMensajeRespuesta(e?.target?.value)}
                      onKeyDown={handleKeyDownRespuesta}
                      placeholder={
                        convSeleccionada?.estado === 'cerrado'
                          ? 'Conversación cerrada'
                          : !['whatsapp', 'instagram', 'email'].includes(convSeleccionada?.canal)
                            ? 'Envío todavía no conectado para este canal'
                            : convSeleccionada?.modo !== 'humano'
                              ? 'Toma la conversación para responder'
                              : 'Escribe un mensaje…'
                      }
                      disabled={
                        convSeleccionada?.estado === 'cerrado' ||
                        !['whatsapp', 'instagram', 'email'].includes(convSeleccionada?.canal) ||
                        convSeleccionada?.modo !== 'humano' ||
                        enviandoMensaje
                      }
                      rows={1}
                      maxLength={4096}
                      className="w-full font-oranienbaum text-sm text-white/80 placeholder-white/20 outline-none bg-transparent resize-none disabled:text-white/30"
                      style={{ minHeight: 24, maxHeight: 96 }}
                    />
                  </div>
                  <button
                    onClick={handleEnviarMensaje}
                    disabled={
                      !mensajeRespuesta?.trim() ||
                      enviandoMensaje ||
                      convSeleccionada?.estado === 'cerrado' ||
                      !['whatsapp', 'instagram', 'email'].includes(convSeleccionada?.canal) ||
                      convSeleccionada?.modo !== 'humano'
                    }
                    className="flex-shrink-0 p-2.5 rounded-xl transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{ background: 'rgba(249,115,22,0.15)', color: ACCENT, border: '1px solid rgba(249,115,22,0.20)' }}
                    title={enviandoMensaje ? 'Enviando…' : `Enviar por ${convSeleccionada?.canal === 'instagram' ? 'Instagram' : convSeleccionada?.canal === 'email' ? 'Email' : 'WhatsApp'}`}>
                    {enviandoMensaje
                      ? <div className="w-4 h-4 border-2 border-orange-400/30 border-t-orange-400 rounded-full animate-spin" />
                      : <Icon name="Send" size={16} />}
                  </button>
                </div>
                <p className="font-oranienbaum text-white/18 text-xs mt-1.5 text-center">
                  {['whatsapp', 'instagram', 'email'].includes(convSeleccionada?.canal) && convSeleccionada?.modo === 'humano' && convSeleccionada?.estado !== 'cerrado'
                    ? 'Enter para enviar · Shift + Enter para salto de línea'
                    : convSeleccionada?.modo !== 'humano' && convSeleccionada?.estado !== 'cerrado'
                      ? 'La IA está activa · toma la conversación para responder manualmente'
                      : !['whatsapp', 'instagram', 'email'].includes(convSeleccionada?.canal)
                        ? 'Envío manual pendiente de conectar para este canal'
                        : ''}
                </p>
              </div>
            </>
          )}

          {/* Panel detalles lateral */}
          {showDetalles && convSeleccionada && (
            <DetallesPanel conv={convSeleccionada} onClose={() => setShowDetalles(false)} />
          )}
        </div>
      </div>
      {showVincularCliente && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.72)' }}>
          <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#111', border: '1px solid rgba(255,255,255,0.10)' }}>
            <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div>
                <h3 className="font-oranienbaum text-white text-lg">Vincular cliente</h3>
                <p className="font-oranienbaum text-white/35 text-xs mt-1">El canal quedará asociado para reconocerlo automáticamente en futuros mensajes.</p>
              </div>
              <button onClick={() => setShowVincularCliente(false)} className="p-2 text-white/40"><Icon name="X" size={17} /></button>
            </div>
            <div className="p-4">
              <input value={busquedaClienteVincular} onChange={e => setBusquedaClienteVincular(e.target.value)}
                placeholder="Buscar cliente por nombre, teléfono o email…"
                autoFocus className="w-full rounded-xl px-4 py-3 bg-white/5 text-white/80 placeholder-white/25 outline-none font-oranienbaum text-sm"
                style={{ border: '1px solid rgba(255,255,255,0.09)' }} />
              <div className="mt-3 max-h-80 overflow-y-auto">
                {clientesVincular.filter(c => {
                  const q = busquedaClienteVincular.trim().toLowerCase();
                  if (!q) return true;
                  return [c?.nombre, c?.nombre_comercial, c?.telefono, c?.email].some(v => String(v || '').toLowerCase().includes(q));
                }).map(cliente => (
                  <button key={cliente.id} disabled={vinculandoCliente} onClick={() => handleVincularCliente(cliente)}
                    className="w-full text-left px-4 py-3 rounded-xl mb-1 transition-all disabled:opacity-50 hover:bg-white/5">
                    <div className="font-oranienbaum text-white/85 text-sm">{cliente.nombre_comercial || cliente.nombre}</div>
                    <div className="font-oranienbaum text-white/30 text-xs mt-0.5">{[cliente.telefono, cliente.email].filter(Boolean).join(' · ') || 'Sin teléfono/email'}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ComercialComunicaciones;
